<template>
  <!-- Page wrapper -->
  <v-layout
    align-center
    justify-center
    role="region"
    aria-label="Reset-password section"
    class="reset-password"
  >
    <v-flex
      xs11
      sm11
      md11
      md4
      lg4
    >
      <!-- Main card -->
      <v-card
        color="secondary"
        elevation="2"
      >
        <!-- Header bar -->
        <v-app-bar flat>
          <v-toolbar-title
            class="text-body-2"
            role="heading"
            aria-level="1"
          >
            {{ $t('components.resetPassFinal.title') }}
          </v-toolbar-title>

          <v-spacer />

          <!-- “Sign in” quick-link -->
          <v-tooltip bottom>
            <template #activator="{ on, attrs }">
              <v-btn
                small
                color="accent"
                :aria-label="$t('components.resetPassFinal.buttons.signin')"
                :to="toSignInLink"
                v-bind="attrs"
                v-on="on"
              >
                {{ $t('components.resetPassFinal.buttons.signin') }}
              </v-btn>
            </template>
            <span>{{ $t('components.resetPassFinal.buttons.signin') }}</span>
          </v-tooltip>
        </v-app-bar>

        <!-- Body -->
        <v-container
          fluid
          grid-list-md
        >
          <!-- Loading indicator -->
          <v-progress-linear
            v-show="isLoading"
            color="accent"
            indeterminate
            role="status"
            aria-label="Resetting your password—please wait"
            class="mb-4"
          />

          <!-- Consolidated validation or server errors -->
          <div
            v-for="(errorsGroup, idx) in filteredErrors"
            :key="idx"
            aria-live="assertive"
            role="status"
          >
            <v-alert
              v-for="error in errorsGroup"
              :key="error.id"
              :type="error.type"
              :icon="error.icon"
              role="alert"
            >
              {{ error.msg }}
            </v-alert>
          </div>

          <!-- Reset password form -->
          <v-form
            role="form"
            aria-labelledby="reset-password-heading"
            @submit.prevent="validate"
          >
            <!-- Email -->
            <v-layout>
              <v-flex xs12>
                <v-text-field
                  v-model="form.email"
                  v-validate="'required|email|min:6|max:255'"
                  prepend-icon="fal fa-envelope"
                  color="accent"
                  data-vv-name="email"
                  :label="$t('components.resetPassFinal.form.email') + ' *'"
                  :error-messages="errors.collect('email')"
                  name="email"
                  autocomplete="email"
                  type="email"
                  aria-required="true"
                />
              </v-flex>
            </v-layout>

            <!-- New password -->
            <v-layout>
              <v-flex xs12>
                <v-text-field
                  ref="newPassword"
                  v-model="form.newPassword"
                  v-validate="'required|min:6|max:50'"
                  prepend-icon="fal fa-key"
                  color="accent"
                  data-vv-name="newPassword"
                  :label="$t('components.resetPassFinal.form.password') + ' *'"
                  :error-messages="errors.collect('newPassword')"
                  name="newPassword"
                  type="password"
                  autocomplete="new-password"
                  aria-required="true"
                />
              </v-flex>
            </v-layout>

            <!-- Confirm new password -->
            <v-layout>
              <v-flex xs12>
                <v-text-field
                  v-model="form.confirmNewPassword"
                  v-validate="'required|min:6|max:50|confirmed:newPassword'"
                  prepend-icon="fal fa-key"
                  color="accent"
                  data-vv-name="confirmNewPassword"
                  :label="$t('components.resetPassFinal.form.confirmPass') + ' *'"
                  :error-messages="errors.collect('confirmNewPassword')"
                  name="confirmNewPassword"
                  type="password"
                  autocomplete="new-password"
                  aria-required="true"
                />
              </v-flex>
            </v-layout>

            <!-- Action row -->
            <v-card-actions>
              <v-spacer />
              <v-btn
                color="accent"
                type="submit"
                :aria-label="$t('components.resetPassFinal.buttons.reset')"
              >
                {{ $t('components.resetPassFinal.buttons.reset') }}
              </v-btn>
            </v-card-actions>
          </v-form>
        </v-container>
      </v-card>
    </v-flex>
  </v-layout>
</template>

<script>
import { ValidationProvider } from "vee-validate";
import { filterByKeys } from "@/utils/object.utils";
import Validator from "@/modules/validator.module";
import blacklist from "@/constants/blacklist";
import links from "@/constants/links";

const ValidatorModule = new Validator();

export default {
  name: "ResetPasswordLayout",
  components: {
    // eslint-disable-next-line vue/no-unused-components
    ValidationProvider
  },
  inject: ["$validator"],
  data() {
    return {
        form: {
        email: "",
            newPassword: "",
            confirmNewPassword: ""
        },
        oAuthLinks: links.oAuth,
        isLoading: false
    }
  },
  computed: {
    filteredErrors() {
      return filterByKeys(
        this.$validator.errors.collect(),
        blacklist.forms.resetPassword
      );
    },
    toSignInLink() {
      return links.routes.login;
    }
  },
  methods: {
    validate() {
      this.isLoading = true;
      this.$validator.validateAll().then(result => {
        if (result) {
          this.$api
            .resetPassword(
              this.form.email,
              this.form.newPassword,
              this.form.confirmNewPassword,
              this.$route.params.token
            )
            .then(res => {
              this.isLoading = false;
              // Add to the validator
              ValidatorModule.add("resetPassword", res);
              // go in 2.5s
              setTimeout(() => {
                window.location.href = `${window.location.protocol}//${window.location.hostname}${links.routes.profileOverview}`;
              }, 4000);
            })
            .catch(data => {
              this.isLoading = false;
            });
        } else {
          this.isLoading = false;
        }
      });
    }
  }
};
</script>

<style lang="scss">
@import "~/_variables.scss";

.reset-password {
  background-image: url("https://gm.goz.fun/wp-content/uploads/2023/03/reset-password-cover.jpg");
  background-size: cover;
  background-position: center center;
}
</style>
